import {jsx, ParamComponent} from 'panel';
import moment from 'moment';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {DummyUrlManager} from 'common/contexts/url-manager/dummy';
import {IronContextRegistry} from 'common/report/context-registry';
import {SearchResult, SearchResultType} from 'common/report/util/search/types';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {useEventListener} from 'common/util/component-helpers';
import {appendBrowseSource, getDisplayContext, getSearchIconFromDisplayContext} from 'common/report/util/search';
import {BrowseMode} from 'common/widgets/browse/types';
import {TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {Placement} from 'common/components/tooltip/types';

import {BrowseCreatorWithViews} from '../mixins';
import css from './index.inline.styl';
import {getEntityImage} from './util';
import {DashboardThumbnailEl} from './dashboard-thumbnail';

type EntityGalleryParams = {
  disableEntityIcons?: boolean;
  emptyMessage?: string;
  mode: BrowseMode;
  isLoading: boolean;
  nameLabel: string;
  userId: number;
  stickyTitles?: boolean;
  entities: SearchResult[];
  onEntityClick?: (entity: SearchResult) => void;
};

const getEntityIcon = (entity: SearchResult) => {
  return getSearchIconFromDisplayContext(getDisplayContext(entity));
};

// TODO: check if we can unify this with the table scroll logic
const PROGRESSIVE_HEIGHT_BUFFER_PX = 920;
const PROGRESSIVE_SIZE_ROWS = 12;

const EntityCard = ({entity, cmp}: {entity: SearchResult; cmp: EntityGallery}) => {
  const modifiedAsMoment = moment.utc(entity.last_edited).local();

  const entityIcon = getEntityIcon(entity);

  const isEntitySharedWithUser = entity.shared_with_user;

  const href =
    cmp.params.mode === BrowseMode.Discover && entity.type !== SearchResultType.Dashboard
      ? appendBrowseSource(entity.link, cmp.getContext(`urlManager`))
      : entity.link;

  const entityCardImage =
    entity.type === SearchResultType.Dashboard && entity.thumbnail_layout ? (
      <DashboardThumbnailEl params={{layout: entity.thumbnail_layout, contents: entity.thumbnail_contents}} />
    ) : (
      <img
        sel='.entity-card-image'
        attrs={{src: getEntityImage(entity.chart_type, entity.report, false, false, !isEntitySharedWithUser)}}
      />
    );

  return (
    <a
      sel='.entity-card'
      class={{
        disabled: !isEntitySharedWithUser,
      }}
      attrs={{href, elref: `entityCard`}}
      on={{
        click: (ev) => {
          if (isEntitySharedWithUser) {
            cmp.params.onEntityClick?.(entity);
          } else {
            ev.preventDefault();
            ev.stopPropagation();
          }
        },
      }}
    >
      <div sel='.entity-card-header'>
        {entityCardImage}
        <SvgIconEl sel='.entity-card-icon' attrs={{icon: entityIcon}} />
      </div>
      <div sel='.entity-card-title'>
        <div sel='.entity-card-name'>{entity.name}</div>

        {!isEntitySharedWithUser ? (
          <>
            <TextHighlightEl
              attrs={{
                text: `No access`,
                theme: TEXT_HIGHLIGHT_THEME.GENERIC,
              }}
            />
            <TooltipEl attrs={{placement: Placement.Bottom}}>This content is not shared with you.</TooltipEl>
          </>
        ) : null}
      </div>
      <div sel='.entity-card-description'>
        {!!entity.description && (
          <>
            <div sel='.text'>{entity.description}</div>
            <TooltipEl
              attrs={{
                'max-width': `200px`,
                text: entity.description,
              }}
            />
          </>
        )}
      </div>

      <div sel='.entity-card-footer'>
        <div sel='.entity-card-edit-time'>{`Last edited ${modifiedAsMoment.fromNow()}`}</div>
        <div sel='.entity-card-edit-user'>
          <BrowseCreatorWithViews entity={entity} />
        </div>
      </div>
    </a>
  );
};

const SkeletonCard = () => (
  <div sel='.entity-card'>
    <div sel='.mp-skeleton-large' />
  </div>
);

type EntityGalleryState = {
  numOfEntitiesToDisplay: number;
};

export class EntityGallery extends ParamComponent<EntityGalleryParams, EntityGalleryState, IronContextRegistry> {
  get config() {
    return {
      css,
      contexts: [`urlManager` as const, `currentProject` as const],
      params: {
        disableEntityIcons: Boolean,
        emptyMessage: String,
        mode: String,
        isLoading: Boolean,
        nameLabel: String,
        userId: Number,
        stickyTitles: Boolean,
        entities: Array,
        onEntityClick: Function,
      },
      defaultContexts: {
        urlManager: new DummyUrlManager(),
      },
      defaultState: {
        numOfEntitiesToDisplay: 16,
      },
      useShadowDom: true,
      template: () => {
        if (this.params.isLoading) {
          return (
            <div sel='.entity-gallery-container.loading'>
              {new Array(12).fill(null).map(() => (
                <SkeletonCard />
              ))}
            </div>
          );
        }
        return (
          <div sel='.entity-gallery-container'>
            {this.params.entities.slice(0, this.state.numOfEntitiesToDisplay).map((entity) => (
              <EntityCard entity={entity} cmp={this} />
            ))}
          </div>
        );
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    useEventListener(this, window, `scroll`, this.handleScroll);
  }

  handleScroll = () => {
    const scrollTop = window.scrollY;
    const scrollBottom = scrollTop + window.innerHeight;
    const scrollHeight = document.body.clientHeight;
    if (scrollBottom + PROGRESSIVE_HEIGHT_BUFFER_PX >= scrollHeight) {
      const numOfEntitiesToDisplay =
        Math.min(this.state.numOfEntitiesToDisplay, this.params.entities.length) + PROGRESSIVE_SIZE_ROWS;
      if (numOfEntitiesToDisplay !== this.state.numOfEntitiesToDisplay) {
        this.update({numOfEntitiesToDisplay});
      }
    }
  };
}

export const EntityGalleryEl = defineAndMakeJsxFactory(`entity-gallery-widget`, EntityGallery);
export const EntityGalleryReact = panel2react(`entity-gallery-widget`, EntityGallery);
